import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useOrders } from '../context/OrderContext';
import { useAuth } from '../context/AuthContext';
import { useStyleCredits } from '../context/StyleCreditsContext';
import { httpsCallable } from 'firebase/functions';
import { functions, functionsAsiaSouth1 } from '../firebase/config';
import { FaCreditCard, FaLock, FaChevronDown, FaMapMarkerAlt, FaUser, FaEnvelope, FaPhone, FaHome,FaStar,FaPlus, FaTag, FaCheckCircle, FaTimes } from 'react-icons/fa';
import StyleCreditsDisplay from '../components/StyleCreditsDisplay';
import LuxuryCashbackDisplay from '../components/LuxuryCashbackDisplay';
import './Checkout.css';
import { doc, getDoc, updateDoc, setDoc, arrayUnion, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import Loader from '../components/Loader';

const Checkout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { items: cartItems = [], clearCart } = useCart(); // eslint-disable-line no-unused-vars
  const { createOrder,updateOrder } = useOrders();
  const { currentUser } = useAuth();
  const { useCredits: applyCredits, credits, awardCredits } = useStyleCredits();
  
  // State for address handling and form
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [allAddresses, setAllAddresses] = useState([]);
  const [showNewAddressForm, setShowNewAddressForm] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    landmark: '',
    state: '',
    pinCode: '',
    saveAddress: false,
    setAsDefault: false
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentPhase, setPaymentPhase] = useState('');
  const [isOrderSummaryExpanded, setIsOrderSummaryExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [validationErrors, setValidationErrors] = useState({});
  const [createdOrderId, setCreatedOrderId] = useState(null); // eslint-disable-line no-unused-vars
  const createdOrderIdRef = useRef(null);
  const hasRedirectedRef = useRef(false);
  
  // Guest checkout state
  const [checkoutType, setCheckoutType] = useState('guest'); // 'guest' or 'login'
  
  
  // Style Credits application state
  const [appliedCredits, setAppliedCredits] = useState(0);
  
  // Coupon state management
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponMessage, setCouponMessage] = useState('');
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);
  const [couponMessageType, setCouponMessageType] = useState(''); // 'success' or 'error'
  
  // Style Credits feedback state
  const [creditsMessage, setCreditsMessage] = useState('');
  const [creditsMessageType, setCreditsMessageType] = useState(''); // 'success' or 'error'
  

  // Handle Buy Now flow - get items from location state or cart
  const getCheckoutItems = () => {
    if (location.state?.isBuyNow && location.state?.buyNowItem) {
      // Buy Now flow - only show the single item
      return [location.state.buyNowItem];
    }
    // Normal cart flow - show all cart items
    return cartItems || [];
  };

  const checkoutItems = getCheckoutItems();

  // Coupon validation function
  const validateCoupon = async (code) => {
    try {
      const discountsRef = collection(db, 'discounts');
      const q = query(discountsRef, where('name', '==', code.toUpperCase()));
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return { valid: false, message: 'Invalid coupon code. Please check and try again.' };
      }
      
      const discount = querySnapshot.docs[0].data();
      
      // Check if discount has scope 'coupon'
      if (discount.scope !== 'coupon') {
        return { valid: false, message: 'This code is not a valid coupon code.' };
      }
      
      // Check if coupon is active
      if (!discount.isActive) {
        return { valid: false, message: 'This coupon code is no longer active.' };
      }
      
      // Check minimum order amount
      const subtotal = getSubtotal();
      if (discount.minOrderAmount && subtotal < discount.minOrderAmount) {
        return { 
          valid: false, 
          message: `Minimum order amount of ₹${discount.minOrderAmount.toLocaleString()} required for this coupon.` 
        };
      }
      
      // Check date validity
      const today = new Date().toISOString().split('T')[0];
      if (discount.startDate && today < discount.startDate) {
        return { valid: false, message: 'This coupon is not yet valid.' };
      }
      if (discount.endDate && today > discount.endDate) {
        return { valid: false, message: 'This coupon has expired.' };
      }
      
      // Return discount in the format expected by the rest of the code
      return { 
        valid: true, 
        discount: {
          code: discount.name,
          type: discount.type,
          value: discount.value,
          active: discount.isActive
        }
      };
    } catch (error) {
      console.error('Error validating coupon:', error);
      return { valid: false, message: 'Error validating coupon. Please try again.' };
    }
  };

  // Apply coupon function
  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponMessage('Please enter a coupon code.');
      setCouponMessageType('error');
      return;
    }

    setIsApplyingCoupon(true);
    setCouponMessage('');

    try {
      const result = await validateCoupon(couponCode.trim());
      
      if (result.valid) {
        setAppliedCoupon(result.discount);
        const savedAmount = result.discount.type === 'percentage' ? 
          Math.min(getSubtotal() * result.discount.value / 100, getSubtotal()) : 
          Math.min(result.discount.value, getSubtotal());
        setCouponMessage(`🎉 Coupon applied successfully! You saved ₹${savedAmount.toLocaleString()}`);
        setCouponMessageType('success');
        setCouponCode(''); // Clear input after successful application
        
        // Auto-hide success message after 4 seconds
        setTimeout(() => {
          setCouponMessage('');
          setCouponMessageType('');
        }, 4000);
      } else {
        setCouponMessage(result.message);
        setCouponMessageType('error');
        
        // Auto-hide error message after 5 seconds
        setTimeout(() => {
          setCouponMessage('');
          setCouponMessageType('');
        }, 5000);
      }
    } catch (error) {
      setCouponMessage('Unable to validate coupon. Please try again.');
      setCouponMessageType('error');
      
      // Auto-hide error message after 5 seconds
      setTimeout(() => {
        setCouponMessage('');
        setCouponMessageType('');
      }, 5000);
    } finally {
      setIsApplyingCoupon(false);
    }
  };

  // Remove coupon function
  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponMessage('');
    setCouponMessageType('');
  };

  // Calculate coupon discount amount
  const getCouponDiscount = () => {
    if (!appliedCoupon) {
      return 0;
    }
    
    const subtotal = getSubtotal();
    if (appliedCoupon.type === 'percentage') {
      return Math.round(Math.min(subtotal * appliedCoupon.value / 100, subtotal)); // Cap at 100% of subtotal
    } else {
      return Math.round(Math.min(appliedCoupon.value, subtotal)); // Cap fixed discount at subtotal
    }
  };

  // Handle loading and redirect if cart is empty
  useEffect(() => {
    if (cartItems !== undefined) {
      setIsLoading(false);
      if (!checkoutItems || checkoutItems.length === 0) {
        navigate('/cart');
      } else {
        // Track checkout page view with Meta pixel
        if (window.fbq) {
          window.fbq('track', 'InitiateCheckout', {
            value: getTotal(),
            currency: 'INR',
            content_ids: checkoutItems.map(item => item.id || item.name),
            content_type: 'product',
            num_items: checkoutItems.reduce((total, item) => total + item.quantity, 0)
          });
        }
      }
    }
  }, [cartItems, checkoutItems, navigate]);

  // No longer redirect to login - allow guest checkout

  // Initialize email field based on user status
  useEffect(() => {
    if (currentUser?.email) {
      setFormData(prev => ({
        ...prev,
        email: currentUser.email
      }));
      setCheckoutType('login');
    } else {
      setCheckoutType('guest');
      // For guest users, ensure form is shown
      setShowNewAddressForm(true);
    }
  }, [currentUser]);

  // Fetch user's saved addresses
  useEffect(() => {
    const fetchUserAddresses = async () => {
      // For guest users, always show new address form
      if (!currentUser?.uid) {
        setShowNewAddressForm(true);
        setCheckoutType('guest');
        return;
      }
      
      // For logged-in users, fetch their addresses and don't show new form by default
      if (currentUser?.uid) {
        setCheckoutType('login');
        try {
          const userRef = doc(db, 'users', currentUser.uid);
          const userDoc = await getDoc(userRef);
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const savedAddresses = userData.saved_addresses || [];
            
            if (savedAddresses.length === 0) {
              // No saved addresses - show new address form directly
              setShowNewAddressForm(true);
              setCheckoutType('login'); // Still a logged-in user
              return;
            }
            
            // Store all addresses
            setAllAddresses(savedAddresses);
            
            // Don't show new address form - show saved addresses
            setShowNewAddressForm(false);
            
            // Find default address if exists
            const defaultAddress = savedAddresses.find(addr => addr.default);
            
            if (defaultAddress) {
              // Auto-select default address
              setSelectedAddress(defaultAddress);
            }
            // If no default, don't auto-select - let user choose
          }
        } catch (error) {
          console.error('Error fetching addresses:', error);
          // If there's an error fetching addresses, show new address form
          setShowNewAddressForm(true);
          setCheckoutType('login'); // Still a logged-in user
        }
      }
    };

    fetchUserAddresses();
  }, [currentUser, navigate]);

  // Function to save address to user profile
  const saveAddressToProfile = async (addressData) => {
    if (!currentUser?.uid || !formData.saveAddress) {
      return;
    }

    try {
      const userRef = doc(db, 'users', currentUser.uid);
      
      // Create address object with unique ID and timestamp
      const newAddress = {
        id: `addr_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        firstName: addressData.firstName,
        lastName: addressData.lastName,
        email: addressData.email,
        phone: addressData.phone,
        address: addressData.address,
        city: addressData.city,
        state: addressData.state,
        pinCode: addressData.pinCode,
        landmark: addressData.landmark || '',
        default: formData.setAsDefault,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      // If setting as default, remove default from other addresses
      if (formData.setAsDefault) {
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const existingAddresses = userData.saved_addresses || [];
          
          // Remove default flag from all existing addresses
          const updatedAddresses = existingAddresses.map(addr => ({
            ...addr,
            default: false
          }));
          
          // Add new address and update user document
          await setDoc(userRef, {
            saved_addresses: [...updatedAddresses, newAddress]
          }, { merge: true });
        }
      } else {
        // Just add the new address without changing defaults
        await setDoc(userRef, {
          saved_addresses: arrayUnion(newAddress)
        }, { merge: true });
      }


    } catch (error) {
      console.error('Error saving address:', error);
      // Don't throw error here - address saving failure shouldn't stop checkout
    }
  };


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Handle phone number input - automatically add +91 prefix
    if (name === 'phone') {
      let phoneValue = value;
      
      // Remove any existing +91 prefix first
      if (phoneValue.startsWith('+91')) {
        phoneValue = phoneValue.substring(3);
      }
      
      // If user enters digits, automatically add +91 prefix
      if (phoneValue.length > 0 && /^\d+$/.test(phoneValue)) {
        phoneValue = '+91' + phoneValue;
      }
      
      setFormData(prev => ({
        ...prev,
        [name]: phoneValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    // Clear validation error when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    // Phone validation - handle +91 prefix
    let phoneToValidate = formData.phone;
    if (phoneToValidate.startsWith('+91')) {
      phoneToValidate = phoneToValidate.substring(3);
    }
    
    const phoneRegex = /^[6-9]\d{9}$/;
    if (!formData.phone) {
      errors.phone = 'Phone number is required';
    } else if (!phoneRegex.test(phoneToValidate)) {
      errors.phone = 'Please enter a valid 10-digit Indian phone number starting with 6, 7, 8, or 9';
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email) {
      errors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    // PIN code validation
    const pinRegex = /^[1-9][0-9]{5}$/;
    if (!formData.pinCode) {
      errors.pinCode = 'PIN code is required';
    } else if (!pinRegex.test(formData.pinCode)) {
      errors.pinCode = 'Please enter a valid 6-digit PIN code';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const scrollToFirstError = () => {
    
    // Find the first field with an error
    const errorFields = Object.keys(validationErrors).filter(field => validationErrors[field]);
    
    if (errorFields.length > 0) {
      const firstErrorField = errorFields[0];
      const fieldElement = document.getElementById(firstErrorField);
      
      if (fieldElement) {
        fieldElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
        fieldElement.focus();
        
        // Add a temporary highlight effect
        fieldElement.style.boxShadow = '0 0 0 3px rgba(220, 53, 69, 0.3)';
        setTimeout(() => {
          fieldElement.style.boxShadow = '';
        }, 2000);
        return;
      }
    }
    
    // If no validation errors but missing required fields, scroll to first missing field
    const requiredFields = ['firstName', 'lastName', 'email', 'phone', 'address', 'city', 'state', 'pinCode'];
    for (const field of requiredFields) {
      if (!formData[field]) {
        const fieldElement = document.getElementById(field);
        
        if (fieldElement) {
          fieldElement.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
          fieldElement.focus();
          
          // Add a temporary highlight effect
          fieldElement.style.boxShadow = '0 0 0 3px rgba(220, 53, 69, 0.3)';
          setTimeout(() => {
            fieldElement.style.boxShadow = '';
          }, 2000);
          return;
        }
      }
    }
    
  };

  const validateCheckout = () => {

    if (showNewAddressForm) {
      // Use the proper validateForm function
      if (!validateForm()) {
        // Scroll to first error field instead of just showing alert
        setTimeout(() => {
          scrollToFirstError();
        }, 300); // Increased delay to ensure form is rendered
        return false;
      }
      
      // Additional required field checks
      if (!formData.firstName || !formData.lastName || !formData.address || 
          !formData.city || !formData.state) {
        // Scroll to first missing field instead of alert
        setTimeout(() => {
          scrollToFirstError();
        }, 300); // Increased delay to ensure form is rendered
        return false;
      }
    } else if (!selectedAddress) {
      // No address selected - show new address form
      setShowNewAddressForm(true);
      return false;
    }
    return true;
  };

  const toggleOrderSummary = () => {
    setIsOrderSummaryExpanded(!isOrderSummaryExpanded);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateCheckout()) {
      return;
    }
    setIsProcessing(true);
    setPaymentPhase('preparing');

    try {
      // Determine email based on checkout type
      const emailForOrder = showNewAddressForm ? formData.email : selectedAddress.email;

      const orderData = {
        items: checkoutItems,
        totalAmount: getTotal(),
        shippingAddress: showNewAddressForm ? {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: emailForOrder,
          phone: formData.phone, // Phone already has +91 prefix
          address: formData.address,
          city: formData.city,
          state: formData.state,
          pinCode: formData.pinCode,
          landmark: formData.landmark || '',
        } : {
          firstName: selectedAddress.firstName,
          lastName: selectedAddress.lastName,
          email: emailForOrder,
          phone: selectedAddress.phone,
          address: selectedAddress.address,
          city: selectedAddress.city,
          state: selectedAddress.state,
          pinCode: selectedAddress.pinCode,
          landmark: selectedAddress.landmark || '',
        },
        paymentMethod: "razorpay",
        paymentStatus: "pending",
        status: "pending",
        paymentCompletedAt: null,
        // Guest checkout fields
        isGuest: checkoutType === 'guest',
        // Discount fields
        appliedCredits: appliedCredits,
        appliedCoupon: appliedCoupon,
        couponDiscount: getCouponDiscount(),
      };

      // Create order once and save the order to ref
      setPaymentPhase('creating-order');
      const orderResult = await createOrder(orderData);
      if (!orderResult.success) {
        throw new Error("Failed to create order before payment");
      }
      createdOrderIdRef.current = orderResult.order;

      // Save address to user profile if checkbox is checked
      if (showNewAddressForm && formData.saveAddress) {
        await saveAddressToProfile(orderData.shippingAddress);
      }

      const receipt = `order_${orderResult.order.orderNumber || orderResult.order.id || Date.now()}`;

      // Get token only for authenticated users, null for guests
      const token = currentUser ? await currentUser.getIdToken() : null;
      const razorpayOrderData = {
        amount: orderData.totalAmount,
        currency: "INR",
        receipt: receipt,
      };

      setPaymentPhase('preparing-payment');
      
      // Prepare headers - only include Authorization for authenticated users
      const headers = {
        "Content-Type": "application/json",
      };
      
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
      
      const response = await fetch(
        "https://asia-south1-vastrebyvijh-8c454.cloudfunctions.net/createRazorpayOrder",
        {
          method: "POST",
          headers: headers,
          body: JSON.stringify(razorpayOrderData),
        }
      );

      const razorpayOrderResult = await response.json();

      if (!razorpayOrderResult.success) {
        throw new Error(
          razorpayOrderResult.error || "Failed to create Razorpay order"
        );
      }

      const razorpayOrder = razorpayOrderResult.order || {};
      const key_id = razorpayOrder.key_id || razorpayOrderResult.key_id || "";

      if (!razorpayOrder.id) {
        throw new Error("Invalid Razorpay order response - missing order ID");
      }

      // Determine the correct customer data to send
      const customerData = {
        name: showNewAddressForm ? 
          `${formData.firstName} ${formData.lastName}` : 
          `${selectedAddress.firstName} ${selectedAddress.lastName}`,
        email: showNewAddressForm ? 
          formData.email : 
          selectedAddress.email,
        contact: showNewAddressForm ? 
          formData.phone : 
          selectedAddress.phone
      };
      const options = {
        key: key_id,
        amount: razorpayOrder.amount,
        currency: razorpayOrder.currency,
        name: "Vastré by Vijh",
        description: "Premium Bespoke Clothing",
        order_id: razorpayOrder.id,
        image: "/images/monogram/monogram_desktop_1756135549612_0.webp", // Custom logo
        handler: function (response) {
          handlePaymentSuccess(response, orderData, razorpayOrder.id);
        },
        prefill: customerData,
        notes: {
          address: `${formData.address}, ${formData.city}, ${formData.state} - ${formData.pinCode}`,
          razorpayOrderId: razorpayOrder.id,
        },
        theme: {
          color: "#3B0618",
          hide_topbar: true, // Hides the top bar with logo
        },
        modal: {
          ondismiss: function () {
            setIsProcessing(false);
            setPaymentPhase('');
          },
        },
      };

      setPaymentPhase('opening-gateway');
      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      console.error("Payment initialization failed:", error);
      alert(`Payment initialization failed: ${error.message}. Please try again.`);
      setIsProcessing(false);
      setPaymentPhase('');
    }
  };
  

  // ✅ Create order ONLY after payment success
  const handlePaymentSuccess = async (response, orderData, razorpayOrderId) => {
    try {
      setPaymentPhase('verifying-payment');

      if (!createdOrderIdRef.current) {
        throw new Error("Order ID missing - cannot update order after payment");
      }

      // Get token only for authenticated users, null for guests
      const token = currentUser ? await currentUser.getIdToken() : null;

      const verificationData = {
        razorpay_order_id: razorpayOrderId,
        razorpay_payment_id: response.razorpay_payment_id,
        razorpay_signature: response.razorpay_signature,
      };

      // Prepare headers - only include Authorization for authenticated users
      const verifyHeaders = {
        "Content-Type": "application/json",
      };
      
      if (token) {
        verifyHeaders.Authorization = `Bearer ${token}`;
      }

      const verifyResponse = await fetch(
        "https://asia-south1-vastrebyvijh-8c454.cloudfunctions.net/verifyPayment",
        {
          method: "POST",
          headers: verifyHeaders,
          body: JSON.stringify(verificationData),
        }
      );

      if (!verifyResponse.ok) {
        throw new Error("Payment verification failed");
      }

      // Update the existing order document with payment success
      const updateData = {
        razorpayOrderId: razorpayOrderId,
        razorpayPaymentId: response.razorpay_payment_id,
        paymentStatus: "completed",
        status: "confirmed",
        paymentCompletedAt: new Date(),
        updatedAt: new Date(),
      };
      const updateResult = await updateOrder(createdOrderIdRef.current.id, updateData);

      // Track successful purchase with Meta pixel
      if (window.fbq) {
        window.fbq('track', 'Purchase', {
          value: getTotal(),
          currency: 'INR',
          content_ids: checkoutItems.map(item => item.id || item.name),
          content_type: 'product',
          num_items: checkoutItems.reduce((total, item) => total + item.quantity, 0)
        });
      }

      if (!updateResult.success) {
        throw new Error("Failed to update order after payment");
      }

      // Award Style Credits for authenticated users after successful payment

      
      if (currentUser?.uid) {
        try {
          const orderData = {
            totalAmount: getAmountAfterCoupons(), // Use amount after coupons (before credits are applied)
            orderNumber: createdOrderIdRef.current.orderNumber,
            id: createdOrderIdRef.current.id
          };

          
          const creditsResult = await awardCredits(orderData);

          
          if (creditsResult.success) {
            // Show success message to user
            const earnedCredits = Math.max(1, Math.round(orderData.totalAmount * 0.10));
            setCreditsMessage(`🎉 Style Credits awarded! You earned ₹${earnedCredits.toLocaleString()} credits.`);
            setCreditsMessageType('success');
            
            // Auto-hide success message after 5 seconds
            setTimeout(() => {
              setCreditsMessage('');
              setCreditsMessageType('');
            }, 5000);
          } else {
            console.error('❌ FAILED: Credits awarding failed:', creditsResult.error);
            // Show error message to user
            setCreditsMessage('⚠️ Credits may take a moment to appear. Check your wallet if needed.');
            setCreditsMessageType('error');
            
            // Auto-hide error message after 4 seconds
            setTimeout(() => {
              setCreditsMessage('');
              setCreditsMessageType('');
            }, 4000);
          }
        } catch (creditsError) {
          console.error('❌ ERROR: Credits awarding threw exception:', creditsError);
          // Don't fail the payment if credits awarding fails
        }

        // Deduct used credits after successful payment
        if (appliedCredits > 0) {
          try {
            const deductResult = await applyCredits(appliedCredits);
            if (deductResult.success) {
              const orderNumber = orderData?.orderNumber || createdOrderIdRef.current?.orderNumber || 'Unknown';
            } else {
              console.error('❌ FAILED: Credits deduction failed:', deductResult.error);
            }
          } catch (deductError) {
            console.error('❌ ERROR: Credits deduction threw exception:', deductError);
            // Don't fail the payment if credits deduction fails
          }
        }
      } else {
      }

      // Send payment completion notification email to admin
      try {
        const sendOrderNotification = httpsCallable(functionsAsiaSouth1, 'sendOrderNotification');
        
        // Get the actual shipping address from the order data
        const actualShippingAddress = showNewAddressForm ? {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          pinCode: formData.pinCode,
          landmark: formData.landmark || '',
        } : {
          firstName: selectedAddress.firstName,
          lastName: selectedAddress.lastName,
          email: selectedAddress.email,
          phone: selectedAddress.phone,
          address: selectedAddress.address,
          city: selectedAddress.city,
          state: selectedAddress.state,
          pinCode: selectedAddress.pinCode,
          landmark: selectedAddress.landmark || '',
        };

        // Create a complete order data structure for email
        const completeOrderData = {
          id: createdOrderIdRef.current.id,
          orderNumber: createdOrderIdRef.current.orderNumber,
          items: checkoutItems.map(item => ({
            name: item.name || 'Product',
            price: item.price || 0,
            quantity: item.quantity || 1,
            size: item.size || null,
            sizeType: item.sizeType || 'standard',
            customMeasurements: item.customMeasurements || null,
            categoryId: item.categoryId || null,
            categoryName: item.categoryName || null
          })),
          totalAmount: getTotal(),
          shippingAddress: actualShippingAddress,
          userEmail: actualShippingAddress.email, // Use shipping address email
          userDisplayName: `${actualShippingAddress.firstName} ${actualShippingAddress.lastName}`.trim(), // Use shipping address name
          paymentStatus: "completed",
          status: "confirmed",
          razorpayOrderId: razorpayOrderId,
          razorpayPaymentId: response.razorpay_payment_id,
          paymentCompletedAt: new Date(),
          createdAt: new Date().toISOString(),
          paymentMethod: "razorpay"
        };
        
        const emailData = {
          orderData: completeOrderData
        };


        // Validate that we have the minimum required data
        if (!completeOrderData.items || completeOrderData.items.length === 0) {
          throw new Error('No items in order data');
        }
        
        if (!completeOrderData.shippingAddress || !completeOrderData.shippingAddress.firstName || !completeOrderData.shippingAddress.address) {
          throw new Error('Missing shipping address data - need firstName and address');
        }
        
        const emailResult = await sendOrderNotification(emailData); // eslint-disable-line no-unused-vars
      } catch (emailError) {
        console.error('❌ Email Notification Failed:', emailError);
        console.error('❌ Email Error Details:', {
          message: emailError.message,
          code: emailError.code,
          details: emailError.details
        });
        // Don't fail the payment success if email fails
      }

      handleOrderSuccess(createdOrderIdRef.current);
    } catch (error) {
      console.error("Payment verification failed:", error);
      alert(`Payment verification failed: ${error.message}. Please contact support.`);
      setIsProcessing(false);
      setPaymentPhase('');
    }
  };


  const handleOrderSuccess = (order) => {
    // For guest users, navigate to order success page
    // For authenticated users, navigate to order history
    if (order.isGuest) {
      navigate('/order-success', { 
        state: { 
          orderId: order.id,
          orderNumber: order.orderNumber,
          amount: order.totalAmount,
          isGuest: true,
          guestEmail: order.shippingAddress?.email
        }
      });
    } else {
      navigate('/order-history', { 
        state: { 
          message: 'Order placed successfully!',
          orderId: order.id
        }
      });
    }
    
    // Clear cart after navigation to prevent redirect issues (only for normal cart flow, not Buy Now)
    setTimeout(() => {
      if (clearCart && !location.state?.isBuyNow) {
        clearCart();
      }
    }, 100);
  };

  const getSubtotal = () => {
    return checkoutItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getShipping = () => {
    return 0; // Free shipping for premium customers
  };

  const getTotal = () => {
    const couponDiscount = getCouponDiscount();
    return getSubtotal() + getShipping() - appliedCredits - couponDiscount;
  };

  // Calculate potential Style Credits (10% of final amount after coupons, minimum 1)
  // Credits are calculated on: subtotal + shipping - coupon discount (before credits are applied)
  const getAmountAfterCoupons = () => {
    return getSubtotal() + getShipping() - getCouponDiscount();
  };
  const potentialCredits = Math.max(1, Math.round(getAmountAfterCoupons() * 0.10));

  // Handle Style Credits application
  const handleUseCredits = (creditsAmount) => {
    if (!currentUser) {
      return;
    }

    if (creditsAmount <= 0) {
      setAppliedCredits(0);
    } else {
      setAppliedCredits(creditsAmount);
    }
  };


  if (isLoading) {
    return (
      <div className="checkout-page">
        <div className="checkout-container">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading checkout...</p>
          </div>
        </div>
      </div>
    );
  }

  // Don't redirect to cart if we're in the middle of processing an order
  if (!checkoutItems || checkoutItems.length === 0) {
    // Check if we're processing an order
    if (isLoading || isProcessing) {
      return (
        <div className="checkout-page">
          <div className="checkout-container">
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Processing your order...</p>
            </div>
          </div>
        </div>
      );
    }
    return null; // Will redirect to cart
  }

  // Show payment loader during processing
  if (isProcessing && paymentPhase) {
    return (
      <div className="checkout-page">
        <div className="checkout-container">
          <Loader isLoading={true} />
          <div className="payment-status">
            <h2>Processing Your Payment</h2>
            <p className="payment-phase">
              {paymentPhase === 'preparing' && '🔄 Preparing your order...'}
              {paymentPhase === 'creating-order' && '📝 Creating your order...'}
              {paymentPhase === 'preparing-payment' && '💳 Preparing payment gateway...'}
              {paymentPhase === 'opening-gateway' && '🚀 Opening payment gateway...'}
              {paymentPhase === 'verifying-payment' && '✅ Verifying payment...'}
            </p>
            <p className="payment-note">Please wait while we process your payment securely</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-page">
      <div className="checkout-container">

        {/* Header */}
        <div className="checkout-header">
          <h1>Complete Your Order</h1>
          <p>Enter your details to complete your premium purchase</p>
        </div>

        <div className="checkout-content">
          {/* Shipping Address Section */}
          <div className="checkout-form-section">
            <h2>Shipping Address</h2>
            
            {/* Guest Checkout Option */}
            {!currentUser && (
              <div className="vastre-guest-checkout-option">
                <div className="vastre-checkout-type-selector compact">
                  <div className="vastre-checkout-type-header">
                    <h3>Checkout Options</h3>
                  </div>
                  
                  <div className="vastre-checkout-type-buttons">
                    <button
                      type="button"
                      className={`vastre-checkout-type-btn ${checkoutType === 'login' ? 'primary active' : 'secondary'}`}
                      onClick={() => {
                        setCheckoutType('login');
                        navigate('/login?returnUrl=' + encodeURIComponent('/checkout'));
                      }}
                    >
                      <div className="vastre-btn-icon">👤</div>
                      <div className="vastre-btn-content">
                        <h4>Login to Account</h4>
                        <p>Saved addresses & order history</p>
                      </div>
                    </button>
                    
                    <button
                      type="button"
                      className={`vastre-checkout-type-btn ${checkoutType === 'guest' ? 'primary active' : 'secondary'}`}
                      onClick={() => {
                        setCheckoutType('guest');
                        setShowNewAddressForm(true);
                        setSelectedAddress(null);
                      }}
                    >
                      <div className="vastre-btn-icon">🛒</div>
                      <div className="vastre-btn-content">
                        <h4>Continue as Guest</h4>
                        <p>Quick checkout</p>
                      </div>
                    </button>
                  </div>
                </div>
                
              </div>
            )}
            
            {!showNewAddressForm && selectedAddress && currentUser ? (
              /* Show Selected Address */
              <div className="default-address-display">
                <div className="address-header">
                  <h3>
                    🚚 {selectedAddress.default ? 'Default Address' : 'Selected Address'}
                  </h3>
                  <div className="header-buttons">
                    <button 
                      type="button"
                      className="change-address-btn"
                      onClick={() => setShowNewAddressForm(true)}
                    >
                      Ship to New Address
                    </button>
                    {allAddresses.length > 1 && (
                      <button 
                        type="button"
                        className="select-other-btn"
                        onClick={() => setSelectedAddress(null)}
                      >
                        Choose Different Address
                      </button>
                    )}
                  </div>
                </div>
                
                <div className="address-card">
                  {selectedAddress.default && (
                    <div className="default-badge">
                      <FaStar /> Default Address
                    </div>
                  )}
                  <div className="address-info">
                    <div className="name-email">
                      <p><FaUser /> {selectedAddress.firstName} {selectedAddress.lastName}</p>
                      <p><FaEnvelope /> {selectedAddress.email}</p>
                      <p><FaPhone /> {selectedAddress.phone}</p>
                    </div>
                    <div className="address-details">
                      <p><FaHome /> {selectedAddress.address}</p>
                      <p><FaMapMarkerAlt /> {selectedAddress.city}, {selectedAddress.state} - {selectedAddress.pinCode}</p>
                      {selectedAddress.landmark && (
                        <p><FaMapMarkerAlt /> Near: {selectedAddress.landmark}</p>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Payment Button for Selected Address */}
                <div className="payment-section">
                  <div className="payment-info">
                    <FaCreditCard className="payment-icon" />
                    <span>Secure payment powered by Razorpay</span>
                  </div>
                  <button 
                    type="button" 
                    className="pay-now-btn"
                    onClick={handleSubmit}
                    disabled={isProcessing}
                  >
                    {isProcessing ? 'Preparing Payment...' : `Pay & Complete Order ₹${getTotal().toLocaleString()}`}
                  </button>
                  
                  {/* Legal Links */}
                  <div className="legal-links">
                    <p className="legal-text">
                      By proceeding, you agree to our{' '}
                      <a href="/terms-of-service" className="legal-link">Terms of Service</a>
                      {' '}and{' '}
                      <a href="/privacy-policy" className="legal-link">Privacy Policy</a>
                    </p>
                  </div>
                </div>
              </div>
            ) : !showNewAddressForm && allAddresses.length > 0 && currentUser ? (
              /* Show Address Selection Interface - Only for logged in users */
              <div className="address-selection-section">
                <div className="selection-header">
                  <h3>📋 Choose Your Shipping Address</h3>
                  <p>Select an address from your saved addresses or add a new one</p>
                </div>
                
                <div className="addresses-grid">
                  {allAddresses.map((address, index) => (
                    <div 
                      key={address.id || index}
                      className={`address-option-card ${address.default ? 'default-option' : ''}`}
                      onClick={() => setSelectedAddress(address)}
                    >
                      {address.default && (
                        <div className="default-option-badge">
                          <FaStar /> Default
                        </div>
                      )}
                      <div className="option-address-info">
                        <h4>{address.firstName} {address.lastName}</h4>
                        <p><FaEnvelope /> {address.email}</p>
                        <p><FaPhone /> {address.phone}</p>
                        <p><FaHome /> {address.address}</p>
                        <p><FaMapMarkerAlt /> {address.city}, {address.state} - {address.pinCode}</p>
                        {address.landmark && (
                          <p><FaMapMarkerAlt /> Near: {address.landmark}</p>
                        )}
                      </div>
                      <div className="select-address-btn">
                        Select This Address
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="add-new-address-section">
                  <button 
                    type="button"
                    className="add-new-address-btn"
                    onClick={() => {
                      setShowNewAddressForm(true);
                    }}
                  >
                    <FaPlus /> Add New Address
                  </button>
                  
                </div>
              </div>
            ) : showNewAddressForm ? (
              /* Show New Address Form */
              <form onSubmit={handleSubmit} className="checkout-form">
                <div className="form-header">
                  <h3>Enter New Shipping Address</h3>
                  {currentUser && (
                    <div className="header-buttons">
                      <button 
                        type="button"
                        className="use-default-btn"
                        onClick={() => setShowNewAddressForm(false)}
                      >
                        Back to Address Selection
                      </button>
                    </div>
                  )}
                </div>
                
                {/* Personal Details */}
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="firstName">First Name *</label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter your first name"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="lastName">Last Name *</label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter your last name"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="email">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter your email address"
                      className={validationErrors.email ? 'error' : ''}
                    />
                    {validationErrors.email && (
                      <div className="error-message">{validationErrors.email}</div>
                    )}
                  </div>
                  <div className="form-group">
                    <label htmlFor="phone">Phone Number *</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      placeholder="+91 Enter your phone number"
                      className={validationErrors.phone ? 'error' : ''}
                    />
                    {validationErrors.phone && (
                      <div className="error-message">{validationErrors.phone}</div>
                    )}
                  </div>
                </div>

                {/* Address Details */}
                <div className="form-group full-width">
                  <label htmlFor="address">Street Address *</label>
                  <input
                    type="text"
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter your street address"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="city">City *</label>
                    <input
                      type="text"
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter your city"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="landmark">Landmark</label>
                    <input
                      type="text"
                      id="landmark"
                      name="landmark"
                      value={formData.landmark}
                      onChange={handleInputChange}
                      placeholder="Enter nearby landmark (optional)"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="state">State *</label>
                    <input
                      type="text"
                      id="state"
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter your state"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="pinCode">PIN Code *</label>
                    <input
                      type="text"
                      id="pinCode"
                      name="pinCode"
                      value={formData.pinCode}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter PIN code"
                      className={validationErrors.pinCode ? 'error' : ''}
                    />
                    {validationErrors.pinCode && (
                      <div className="error-message">{validationErrors.pinCode}</div>
                    )}
                  </div>
                </div>

                {/* Address Save Options - Only for logged in users */}
                {currentUser && (
                  <div className="address-save-options">
                    <div className="address-options-row">
                      <div className="save-address-checkbox">
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={formData.saveAddress}
                            onChange={(e) => {
                              setFormData(prev => ({
                                ...prev,
                                saveAddress: e.target.checked
                              }));
                            }}
                          />
                          <span>Save this address for future orders</span>
                        </label>
                      </div>
                      
                      {formData.saveAddress && (
                        <div className="default-address-checkbox">
                          <label className="checkbox-label premium">
                            <input
                              type="checkbox"
                              checked={formData.setAsDefault}
                              onChange={(e) => {
                                setFormData(prev => ({
                                  ...prev,
                                  setAsDefault: e.target.checked
                                }));
                              }}
                            />
                            <span>⭐ Set as default address</span>
                          </label>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Payment Button */}
                <div className="payment-section">
                  <div className="payment-info">
                    <FaCreditCard className="payment-icon" />
                    <span>Secure payment powered by Razorpay</span>
                  </div>
                  <button 
                    type="submit" 
                    className="pay-now-btn"
                    disabled={isProcessing}
                  >
                    {isProcessing ? 'Preparing Payment...' : `Pay & Complete Order ₹${getTotal().toLocaleString()}`}
                  </button>
                  

                  
                  {/* Legal Links */}
                  <div className="legal-links">
                    <p className="legal-text">
                      By proceeding, you agree to our{' '}
                      <a href="/terms-of-service" className="legal-link">Terms of Service</a>
                      {' '}and{' '}
                      <a href="/privacy-policy" className="legal-link">Privacy Policy</a>
                    </p>
                  </div>
                </div>
              </form>
            ) : currentUser ? (
              /* Show Address Selection Interface - Only for logged in users */
              <div className="address-selection-section">
                <div className="selection-header">
                  <h3>📋 Choose Your Shipping Address</h3>
                  <p>Select an address from your saved addresses or add a new one</p>
                </div>
                
                <div className="addresses-grid">
                  {allAddresses.map((address, index) => (
                    <div 
                      key={address.id || index}
                      className={`address-option-card ${address.default ? 'default-option' : ''}`}
                      onClick={() => setSelectedAddress(address)}
                    >
                      {address.default && (
                        <div className="default-option-badge">
                          <FaStar /> Default
                        </div>
                      )}
                      <div className="option-address-info">
                        <h4>{address.firstName} {address.lastName}</h4>
                        <p><FaEnvelope /> {address.email}</p>
                        <p><FaPhone /> {address.phone}</p>
                        <p><FaHome /> {address.address}</p>
                        <p><FaMapMarkerAlt /> {address.city}, {address.state} - {address.pinCode}</p>
                        {address.landmark && (
                          <p><FaMapMarkerAlt /> Near: {address.landmark}</p>
                        )}
                      </div>
                      <div className="select-address-btn">
                        Select This Address
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="add-new-address-section">
                  <button 
                    type="button"
                    className="add-new-address-btn"
                    onClick={() => {
                      setShowNewAddressForm(true);
                    }}
                  >
                    <FaPlus /> Add New Address
                  </button>

                </div>
              </div>
            ) : (
              /* Show New Address Form for Guest Users or when form is explicitly shown */
              <div className="address-form-section">
                <div className="form-header">
                  <h3>📋 Shipping Address</h3>
                  <p>Please provide your shipping details</p>
                </div>
                
                <form onSubmit={handleSubmit} className="address-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="firstName">First Name *</label>
                      <input
                        type="text"
                        id="firstName"
                        value={formData.firstName}
                        onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="lastName">Last Name *</label>
                      <input
                        type="text"
                        id="lastName"
                        value={formData.lastName}
                        onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone">Phone Number *</label>
                    <input
                      type="tel"
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="address">Street Address *</label>
                    <textarea
                      id="address"
                      value={formData.address}
                      onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                      rows="3"
                      required
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="city">City *</label>
                      <input
                        type="text"
                        id="city"
                        value={formData.city}
                        onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="state">State *</label>
                      <input
                        type="text"
                        id="state"
                        value={formData.state}
                        onChange={(e) => setFormData(prev => ({ ...prev, state: e.target.value }))}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="pinCode">PIN Code *</label>
                      <input
                        type="text"
                        id="pinCode"
                        value={formData.pinCode}
                        onChange={(e) => setFormData(prev => ({ ...prev, pinCode: e.target.value }))}
                        required
                      />
                    </div>
                  </div>

                  {/* Payment Button */}
                  <div className="payment-section">
                    <div className="payment-info">
                      <FaCreditCard className="payment-icon" />
                      <div className="payment-details">
                        <h4>Secure Payment</h4>
                        <p>Your payment information is encrypted and secure</p>
                      </div>
                    </div>
                    
                    <button 
                      type="submit" 
                      className="payment-button"
                      disabled={isProcessing}
                    >
                      {isProcessing ? (
                        <>
                          <div className="spinner"></div>
                          Processing...
                        </>
                      ) : (
                        <>
                          <FaCreditCard className="button-icon" />
                          Pay ₹{getTotal().toLocaleString()}
                        </>
                      )}
                    </button>
                  </div>

                  <div className="legal-notice">
                    <p>
                      By placing this order, you agree to our{' '}
                      <a href="/terms-of-service" className="legal-link">Terms of Service</a>
                      {' '}and{' '}
                      <a href="/privacy-policy" className="legal-link">Privacy Policy</a>
                    </p>
                  </div>
                </form>
              </div>
            )}
          </div>

          {/* Luxury Order Summary */}
          <div className={`luxury-order-summary ${isOrderSummaryExpanded ? 'expanded' : ''}`}>
            <div className="summary-header" onClick={toggleOrderSummary}>
              <h2>
                Order Summary
                <FaChevronDown className="expand-icon" />
              </h2>
              <div className="mobile-summary-preview">
                <span className="total-amount">₹{getTotal().toLocaleString()}</span>
              </div>
            </div>
            
            {/* Product Items - Compact Luxury Layout */}
            <div className="luxury-order-items">
              {checkoutItems.map((item, index) => (
                <div key={index} className="luxury-order-item">
                  <div className="item-image-frame">
                    <img src={item.image} alt={item.name} loading="lazy" />
                    <div className="item-overlay"></div>
                  </div>
                  <div className="item-details-compact">
                    <h4 className="item-name">{item.name}</h4>
                    <div className="item-meta">
                      <span className="item-size">Size {item.size}</span>
                      <span className="item-qty">Qty {item.quantity}</span>
                    </div>
                    {/* <div className="item-price-prominent">
                      ₹{(item.price * item.quantity).toLocaleString()}
                    </div> */}
                  </div>
                </div>
              ))}
            </div>

            {/* Luxury Totals Section */}
            <div className="luxury-totals">
              <div className="total-divider"></div>
              <div className="total-row">
                <span className="total-label">Subtotal</span>
                <span className="total-value">₹{getSubtotal().toLocaleString()}</span>
              </div>
              <div className="total-row shipping">
                <span className="total-label">Shipping</span>
                <span className="free-shipping-badge">FREE</span>
              </div>
              {getCouponDiscount() > 0 && (
                <div className="total-row coupon">
                  <span className="total-label">Coupon ({appliedCoupon?.code})</span>
                  <span className="total-value coupon-value">-₹{getCouponDiscount().toLocaleString()}</span>
                </div>
              )}
              {appliedCredits > 0 && (
                <div className="total-row credits">
                  <span className="total-label">Style Credits</span>
                  <span className="total-value credits-value">-₹{appliedCredits.toLocaleString()}</span>
                </div>
              )}
              <div className="total-row grand-total">
                <span className="total-label">Total</span>
                <span className="total-value-prominent">₹{getTotal().toLocaleString()}</span>
              </div>
            </div>
            
            {/* Coupon Code Section */}
            <div className="vastre-checkout-coupon-section">
              {!appliedCoupon ? (
                <div className="vastre-coupon-input-group" style={{ position: 'relative' }}>
                  {/* Tooltip */}
                  {couponMessage && (
                    <div className={`vastre-coupon-tooltip ${couponMessageType} show`}>
                      {couponMessage}
                    </div>
                  )}
                  
                  <input
                    type="text"
                    placeholder="Enter coupon code"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                    onKeyPress={(e) => e.key === 'Enter' && handleApplyCoupon()}
                    className={`vastre-coupon-input ${couponMessageType || ''}`}
                    disabled={isApplyingCoupon}
                  />
                  <button
                    onClick={handleApplyCoupon}
                    disabled={isApplyingCoupon || !couponCode.trim()}
                    className="vastre-coupon-apply-btn"
                  >
                    {isApplyingCoupon ? 'Applying...' : 'Apply'}
                  </button>
                </div>
              ) : (
                <div className="vastre-applied-coupon-display">
                  <div className="vastre-applied-coupon-info">
                    <FaCheckCircle className="vastre-coupon-check-icon" />
                    <span className="vastre-applied-coupon-text">
                      {appliedCoupon.code} applied - {appliedCoupon.type === 'percentage' ? 
                        `${appliedCoupon.value}% off` : 
                        `₹${appliedCoupon.value} off`}
                    </span>
                  </div>
                  <button
                    onClick={handleRemoveCoupon}
                    className="vastre-remove-coupon-btn"
                    title="Remove coupon"
                  >
                    <FaTimes />
                  </button>
                </div>
              )}
            </div>

            {/* Style Credits Feedback Message */}
            {creditsMessage && (
              <div className={`checkout-credits-feedback-message ${creditsMessageType} show`}>
                <div className="checkout-credits-feedback-content">
                  <FaCreditCard className="checkout-credits-feedback-icon" />
                  <span className="checkout-credits-feedback-text">{creditsMessage}</span>
                </div>
              </div>
            )}

            {/* Style Credits Application for Logged-in Users */}
            {currentUser && (
              <div className="checkout-style-credits-simple">
                <label className={`checkout-credits-checkbox-label ${credits.availableCredits === 0 ? 'disabled' : ''}`}>
                  <input 
                    type="checkbox" 
                    className="checkout-credits-checkbox"
                    checked={appliedCredits > 0}
                    disabled={credits.availableCredits === 0}
                    onChange={(e) => {
                      if (e.target.checked) {
                        // Apply maximum available credits (90% limit)
                        // Use amount after coupons for consistency
                        const orderTotal = getAmountAfterCoupons();
                        const maxCredits = Math.min(credits.availableCredits, Math.floor(orderTotal * 0.9));
                        handleUseCredits(maxCredits);
                      } else {
                        setAppliedCredits(0);
                      }
                    }}
                  />
                  <span className="checkout-credits-checkbox-text">
                    <FaCreditCard className="checkout-credits-icon" />
                    {credits.availableCredits > 0 ? (
                      <>Use Style Credits (₹{Math.max(0, credits.availableCredits).toLocaleString()} available)</>
                    ) : (
                      <>No Style Credits Available</>
                    )}
                  </span>
                </label>
              </div>
            )}

            {/* Credits Earning Preview - Beautiful & Compact */}
            {currentUser && (
              <div className="checkout-credits-earning-preview">
                <div className="credits-earning-icon">
                  <FaCreditCard />
                </div>
                <div className="credits-earning-content">
                  <span className="credits-earning-label">You'll earn</span>
                  <span className="credits-earning-amount">₹{potentialCredits.toLocaleString()}</span>
                  <span className="credits-earning-suffix">Style Credits</span>
                </div>
                <div className="credits-earning-badge">
                  <span className="credits-percentage">10%</span>
                </div>
              </div>
            )}


            {/* Style Credits Highlight for Non-logged-in Users */}
            {!currentUser && (
              <div className="style-credits-highlight">
                <span className="credits-icon">💳</span>
                <span className="credits-text">
                  Earn ₹{potentialCredits.toLocaleString()} Style Credits (expires in 60 days). Only for logged-in members.
                </span>
              </div>
            )}

            {/* Luxury Security & Trust */}
            <div className="luxury-security">
              <div className="security-item">
                <FaLock className="security-icon" />
                <span>256-bit SSL Encryption</span>
              </div>
              <div className="security-item">
                <FaCreditCard className="security-icon" />
                <span>Secure Payment Gateway</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Luxury Cashback Display */}
      <LuxuryCashbackDisplay 
        orderTotal={getAmountAfterCoupons()} 
        position="bottom-right" 
        showFloatingIcon={true}
        autoOpen={true}
        pageKey="checkout"
      />
    </div>
  );
};

export default Checkout; 